import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { useAuth } from '../context/AuthContext';
import { membersAPI } from '../api/api';
import { Users, UserPlus, Copy, Check, Mail, Clock, Shield, Eye, Edit3, Settings as Cog, MoreVertical, UserX, UserCheck, LogOut, CheckCircle2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useCountUp } from '../hooks/useCountUp';

const ROLE_LABELS = { owner: 'Propriétaire', admin: 'Administrateur', manager: 'Manager', editor: 'Éditeur', viewer: 'Lecteur' };
const ROLE_PILL  = { owner: 'violet',         admin: 'blue',           manager: 'success', editor: 'amber',    viewer: '' };
const ROLE_ICONS = { owner: Shield,           admin: Shield,           manager: Cog,       editor: Edit3,     viewer: Eye };

const STATUS_LABELS = { active: 'Actif', invited: 'Invité', disabled: 'Désactivé' };
const STATUS_DOT    = { active: '#4ADE80', invited: '#FBBF24', disabled: '#6B7280' };

const formatDate = (s) => s ? new Date(s).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const Members = () => {
  const { currentOrg, user } = useAuth();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | active | invited | disabled

  // Invite dialog
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('editor');
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [copied, setCopied] = useState(false);

  // Row menu + confirm
  const [actionMemberId, setActionMemberId] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null); // { type, member }
  const [actionLoading, setActionLoading] = useState(false);

  const fetchMembers = useCallback(async () => {
    if (!currentOrg) return;
    try {
      setLoading(true);
      const data = await membersAPI.list();
      setMembers(data);
    } catch {
      toast({ title: 'Erreur', description: 'Impossible de charger les membres', variant: 'destructive' });
    } finally { setLoading(false); }
  }, [currentOrg, toast]);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  useEffect(() => {
    if (searchParams.get('invite') === 'true') {
      setShowInvite(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      const result = await membersAPI.invite({ email: inviteEmail.trim(), role: inviteRole });
      setInviteResult(result);
      toast({
        title: 'Invitation créée',
        description: result.email_sent ? `Email envoyé à ${inviteEmail}` : 'Lien généré (email non envoyé)',
      });
    } catch (err) {
      toast({ title: 'Erreur', description: err.response?.data?.detail || 'Erreur lors de la création', variant: 'destructive' });
    } finally { setInviting(false); }
  };

  const handleCopyLink = async () => {
    if (!inviteResult?.invite_link) return;
    try {
      await navigator.clipboard.writeText(inviteResult.invite_link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { toast({ title: 'Erreur', description: 'Impossible de copier', variant: 'destructive' }); }
  };

  const resetInvite = () => {
    setShowInvite(false); setInviteEmail(''); setInviteRole('editor');
    setInviteResult(null); setCopied(false);
  };

  const myMembership = members.find(m => m.user_id === user?.id);
  const myRole = myMembership?.role;
  const canManage = ['owner', 'admin'].includes(myRole);

  const handleRoleChange = async (member, newRole) => {
    setActionLoading(true);
    try {
      await membersAPI.updateRole(member.id, newRole);
      toast({ title: 'Rôle modifié', description: `${member.full_name || 'Membre'} est maintenant ${ROLE_LABELS[newRole]}` });
      await fetchMembers();
    } catch (err) { toast({ title: 'Erreur', description: err.response?.data?.detail || 'Impossible de modifier le rôle', variant: 'destructive' }); }
    finally { setActionLoading(false); setActionMemberId(null); }
  };

  const handleStatusToggle = async (member) => {
    const newStatus = member.status === 'active' ? 'disabled' : 'active';
    setActionLoading(true);
    try {
      await membersAPI.updateStatus(member.id, newStatus);
      toast({ title: newStatus === 'disabled' ? 'Membre suspendu' : 'Membre réactivé' });
      await fetchMembers();
    } catch (err) { toast({ title: 'Erreur', description: err.response?.data?.detail || 'Impossible', variant: 'destructive' }); }
    finally { setActionLoading(false); setActionMemberId(null); setConfirmDialog(null); }
  };

  const handleRemove = async (member) => {
    setActionLoading(true);
    try {
      await membersAPI.remove(member.id);
      toast({ title: 'Membre retiré', description: `${member.full_name || 'Le membre'} a été retiré.` });
      await fetchMembers();
    } catch (err) { toast({ title: 'Erreur', description: err.response?.data?.detail || 'Impossible', variant: 'destructive' }); }
    finally { setActionLoading(false); setActionMemberId(null); setConfirmDialog(null); }
  };

  // Counts for filter tabs + KPIs
  const counts = {
    all: members.length,
    active: members.filter(m => m.status === 'active').length,
    invited: members.filter(m => m.status === 'invited').length,
    disabled: members.filter(m => m.status === 'disabled').length,
  };
  const filtered = filter === 'all' ? members : members.filter(m => m.status === filter);
  const distinctRoles = new Set(members.map(m => m.role)).size;

  return (
    <AppLayout title="Membres">
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>

        {/* Page head */}
        <div className="sd-page-head" style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1 style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <Users size={20} style={{ color: 'var(--vio2)' }} />
              Membres de l'équipe
            </h1>
            <p>{members.length} membre{members.length !== 1 ? 's' : ''} dans {currentOrg?.name || '—'}</p>
          </div>
          {canManage && (
            <button className="sd-btn sd-btn-primary" onClick={() => setShowInvite(true)}>
              <UserPlus size={13} /> Inviter un membre
            </button>
          )}
        </div>

        {/* Stats row */}
        <div className="sd-stats-row sd-stagger">
          <StatCard label="Total membres" value={counts.all} icon={<Users size={16} />} tone="violet" />
          <StatCard label="Actifs" value={counts.active} icon={<UserCheck size={16} />} tone="green" />
          <StatCard label="Invitations en attente" value={counts.invited} icon={<Mail size={16} />} tone="amber" />
          <StatCard label="Rôles distincts" value={distinctRoles} icon={<Shield size={16} />} tone="blue" />
        </div>

        {/* Filter tabs */}
        <div className="sd-toolbar">
          <div className="sd-filter-tabs">
            {[
              { k: 'all',      label: 'Tous' },
              { k: 'active',   label: 'Actifs' },
              { k: 'invited',  label: 'Invités' },
              { k: 'disabled', label: 'Désactivés' },
            ].map(t => (
              <button key={t.k} className={`sd-filter-tab${filter === t.k ? ' active' : ''}`} onClick={() => setFilter(t.k)}>
                {t.label}<span className="count">{counts[t.k]}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Members table */}
        <div className="sd-dash-section">
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 18 }}>
              {[1,2,3].map(i => <div key={i} className="sd-skeleton" style={{ height: 36 }} />)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="sd-empty-state">
              <div className="sd-empty-icon">👥</div>
              <div className="sd-empty-title">{filter === 'all' ? 'Aucun membre' : `Aucun membre ${filter === 'invited' ? 'invité' : filter === 'disabled' ? 'désactivé' : 'actif'}`}</div>
              <div className="sd-empty-sub">{canManage ? 'Invitez votre premier collègue pour démarrer.' : ' '}</div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    <Th>Membre</Th>
                    <Th>Rôle</Th>
                    <Th>Statut</Th>
                    <Th>Dossiers</Th>
                    <Th>Depuis</Th>
                    {canManage && <Th />}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((member) => {
                    const RoleIcon = ROLE_ICONS[member.role] || Eye;
                    const isMe = member.user_id === user?.id;
                    const isOwner = member.role === 'owner';
                    const displayName = member.full_name || (member.user_id ? member.user_id.slice(0, 8) + '…' : '—');
                    const initials = (displayName.split(' ').map(w => w[0]).join('') || '?').toUpperCase().slice(0, 2);
                    return (
                      <tr key={member.id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <Td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div className="sd-avatar" style={{ background: 'linear-gradient(135deg,#7B68EE,#2563EB)' }}>{initials}</div>
                            <div>
                              <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--white)' }}>
                                {displayName}
                                {isMe && <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--vio2)', fontWeight: 500 }}>(vous)</span>}
                              </div>
                            </div>
                          </div>
                        </Td>
                        <Td>
                          <span className={`sd-pill ${ROLE_PILL[member.role] || ''}`}>
                            <RoleIcon size={10} /> {ROLE_LABELS[member.role] || member.role}
                          </span>
                        </Td>
                        <Td>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: 'var(--muted2)' }}>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: STATUS_DOT[member.status] || '#6B7280' }} />
                            {STATUS_LABELS[member.status] || member.status}
                          </span>
                        </Td>
                        <Td><span style={{ fontSize: 12, color: 'var(--muted2)' }}>{member.dossier_count ?? 0}</span></Td>
                        <Td><span style={{ fontSize: 11.5, color: 'var(--muted)' }}>{formatDate(member.created_at)}</span></Td>
                        {canManage && (
                          <Td>
                            {!isOwner && !isMe && (
                              <div style={{ position: 'relative', display: 'inline-block' }}>
                                <button
                                  onClick={() => setActionMemberId(actionMemberId === member.id ? null : member.id)}
                                  className="sd-action-btn" style={{ width: 28, height: 28, padding: 0, flex: 'none' }}
                                  aria-label="Actions"
                                >
                                  <MoreVertical size={14} />
                                </button>
                                {actionMemberId === member.id && (
                                  <>
                                    <div onClick={() => setActionMemberId(null)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
                                    <div className="sd-menu">
                                      <div className="sd-menu-section">Changer le rôle</div>
                                      {['viewer', 'editor', 'manager', 'admin'].map(r => (
                                        <button key={r}
                                          className="sd-menu-item"
                                          onClick={() => handleRoleChange(member, r)}
                                          disabled={actionLoading || member.role === r}
                                          style={{ justifyContent: 'space-between' }}>
                                          <span style={{ color: member.role === r ? 'var(--vio2)' : 'inherit' }}>{ROLE_LABELS[r]}</span>
                                          {member.role === r && <Check size={12} />}
                                        </button>
                                      ))}
                                      <div className="sd-menu-divider" />
                                      <button className="sd-menu-item"
                                        onClick={() => { setActionMemberId(null); setConfirmDialog({ type: 'status', member }); }}
                                      >
                                        {member.status === 'active' ? <UserX size={12} /> : <UserCheck size={12} />}
                                        {member.status === 'active' ? 'Suspendre' : 'Réactiver'}
                                      </button>
                                      <button className="sd-menu-item danger"
                                        onClick={() => { setActionMemberId(null); setConfirmDialog({ type: 'remove', member }); }}
                                      >
                                        <LogOut size={12} /> Retirer de l'org
                                      </button>
                                    </div>
                                  </>
                                )}
                              </div>
                            )}
                          </Td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Invite modal */}
      {showInvite && (
        <>
          <div className="sd-modal-backdrop" onClick={resetInvite} />
          <div className="sd-modal">
            <div className="sd-modal-panel" onClick={(e) => e.stopPropagation()}>
              {!inviteResult ? (
                <>
                  <div className="sd-modal-head">
                    <span className="sd-modal-title">Inviter un membre</span>
                    <button className="sd-modal-close" onClick={resetInvite}>✕</button>
                  </div>
                  <form onSubmit={handleInvite}>
                    <div className="sd-modal-body sd-form">
                      <div className="sd-field">
                        <label>Adresse email</label>
                        <input type="email" className="sd-input" autoFocus required
                          value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
                          placeholder="collegue@entreprise.com" />
                      </div>
                      <div className="sd-field">
                        <label>Rôle</label>
                        <select className="sd-select" value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}>
                          <option value="viewer">Lecteur — accès en lecture seule</option>
                          <option value="editor">Éditeur — peut créer et modifier</option>
                          <option value="manager">Manager — peut aussi supprimer</option>
                          <option value="admin">Administrateur — gestion complète</option>
                        </select>
                      </div>
                    </div>
                    <div className="sd-modal-foot">
                      <button type="button" className="sd-btn sd-btn-ghost" onClick={resetInvite}>Annuler</button>
                      <button type="submit" disabled={inviting} className="sd-btn sd-btn-primary">
                        {inviting ? '…' : 'Envoyer l\'invitation'}
                      </button>
                    </div>
                  </form>
                </>
              ) : (
                <>
                  <div className="sd-modal-head">
                    <span className="sd-modal-title">Invitation créée</span>
                    <button className="sd-modal-close" onClick={resetInvite}>✕</button>
                  </div>
                  <div className="sd-modal-body">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                      <CheckCircle2 size={18} style={{ color: '#4ADE80' }} />
                      <div>
                        <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--white)' }}>
                          {inviteResult.email_sent ? `Email envoyé à ${inviteEmail}` : 'Email non envoyé'}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--muted2)', marginTop: 1 }}>
                          {inviteResult.email_sent ? 'Le destinataire recevra un lien sous peu.' : 'Partagez le lien manuellement ci-dessous.'}
                        </div>
                      </div>
                    </div>
                    <div className="sd-field">
                      <label>Lien d'invitation</label>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <input className="sd-input" readOnly value={inviteResult.invite_link} style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11 }} />
                        <button onClick={handleCopyLink} className="sd-btn sd-btn-soft" style={{ flexShrink: 0 }}>
                          {copied
                            ? <><span className="sd-pop" style={{ display: 'inline-flex' }}><Check size={12} /></span> Copié</>
                            : <><Copy size={12} /> Copier</>}
                        </button>
                      </div>
                      <p style={{ fontSize: 10.5, color: 'var(--muted)', marginTop: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Clock size={10} /> Expire le {new Date(inviteResult.expires_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </p>
                    </div>
                  </div>
                  <div className="sd-modal-foot">
                    <button className="sd-btn sd-btn-primary" onClick={() => { resetInvite(); fetchMembers(); }}>Terminé</button>
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}

      {/* Confirm dialog */}
      {confirmDialog && (
        <>
          <div className="sd-modal-backdrop" onClick={() => setConfirmDialog(null)} />
          <div className="sd-modal">
            <div className={`sd-modal-panel${confirmDialog.type === 'remove' ? ' danger' : ''}`} style={{ maxWidth: 380 }} onClick={(e) => e.stopPropagation()}>
              <div className="sd-modal-head">
                <span className="sd-modal-title">
                  {confirmDialog.type === 'remove' ? 'Retirer ce membre ?'
                    : confirmDialog.member.status === 'active' ? 'Suspendre ce membre ?' : 'Réactiver ce membre ?'}
                </span>
                <button className="sd-modal-close" onClick={() => setConfirmDialog(null)}>✕</button>
              </div>
              <div className="sd-modal-body">
                <p style={{ fontSize: 12.5, color: 'var(--muted2)' }}>
                  <span style={{ color: 'var(--white)', fontWeight: 600 }}>{confirmDialog.member.full_name || 'Ce membre'}</span>{' '}
                  {confirmDialog.type === 'remove'
                    ? "perdra tout accès à l'organisation."
                    : confirmDialog.member.status === 'active'
                      ? "ne pourra plus accéder à l'organisation."
                      : "pourra à nouveau accéder à l'organisation."}
                </p>
              </div>
              <div className="sd-modal-foot">
                <button className="sd-btn sd-btn-ghost" onClick={() => setConfirmDialog(null)}>Annuler</button>
                <button
                  className={`sd-btn ${confirmDialog.type === 'remove' ? 'sd-btn-danger' : 'sd-btn-primary'}`}
                  disabled={actionLoading}
                  onClick={() => confirmDialog.type === 'remove' ? handleRemove(confirmDialog.member) : handleStatusToggle(confirmDialog.member)}
                >
                  {actionLoading ? '…' : confirmDialog.type === 'remove'
                    ? 'Retirer'
                    : confirmDialog.member.status === 'active' ? 'Suspendre' : 'Réactiver'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </AppLayout>
  );
};

const Th = ({ children }) => (
  <th style={{ textAlign: 'left', padding: '10px 14px', fontSize: 10.5, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.6 }}>
    {children}
  </th>
);
const Td = ({ children }) => (
  <td style={{ padding: '12px 14px', verticalAlign: 'middle' }}>{children}</td>
);

const StatCard = ({ label, value, icon, tone }) => {
  const toneCss = {
    violet: { bg: 'rgba(108,71,255,0.1)', color: 'var(--vio2)' },
    green:  { bg: 'rgba(34,197,94,0.1)',  color: '#4ADE80' },
    amber:  { bg: 'rgba(249,115,22,0.1)', color: '#FB923C' },
    blue:   { bg: 'rgba(59,130,246,0.1)', color: '#60A5FA' },
  }[tone] || { bg: 'rgba(108,71,255,0.1)', color: 'var(--vio2)' };
  const animated = useCountUp(typeof value === 'number' ? value : 0);
  const display = typeof value === 'number' ? animated : value;
  return (
    <div className="sd-stat-card">
      <div className="sd-stat-label">{label}</div>
      <div className="sd-stat-value">{display}</div>
      <div className="sd-stat-icon" style={{ background: toneCss.bg, color: toneCss.color }}>{icon}</div>
    </div>
  );
};

export default Members;
